import { BaseApiService, API_CONFIG } from './base.service';
import {
  Customer,
  CustomerCreate,
  PaginatedResponse
} from '../../types';

/**
 * Service for managing customers
 */
export class CustomerService extends BaseApiService {
  async getCustomers(
    page: number = 1, 
    pageSize: number = 10, 
    customerType?: 'controller' | 'supervisor',
    paymentStatus?: string,
    isSupervised?: boolean,
    search?: string,
    isActive?: boolean
  ): Promise<PaginatedResponse<Customer> | Customer[]> {
    const params: any = {
      skip: ((page - 1) * pageSize).toString(),
      limit: pageSize.toString()
    };
    
    if (customerType) {
      params.customer_type = customerType;
    }
    
    if (paymentStatus) {
      params.payment_status = paymentStatus;
    }

    if (isSupervised !== undefined) {
      params.is_supervised = isSupervised.toString();
    }
    
    if (search) {
      params.search = search;
    }
    
    if (isActive !== undefined) {
      params.is_active = isActive.toString();
    }
    
    const url = `${API_CONFIG.ENDPOINTS.CUSTOMERS}?${new URLSearchParams(params).toString()}`;
    return this.get<PaginatedResponse<Customer> | Customer[]>(url);
  }

  async getCustomer(id: number): Promise<Customer> {
    return this.get(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/${id}`);
  }

  async createCustomer(customer: CustomerCreate): Promise<Customer> {
    return this.post(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/`, customer);
  }

  async updateCustomer(id: number, customer: Partial<CustomerCreate>): Promise<Customer> {
    return this.put(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/${id}`, customer);
  }

  async deleteCustomer(id: number): Promise<void> {
    await this.delete(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/${id}`);
  }

  async bulkDeleteCustomers(customerIds: number[], softDelete: boolean = true): Promise<any> {
    return this.post(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/bulk-delete?soft_delete=${softDelete}`, customerIds);
  }

  async getUnsupervisedCustomers(page: number = 1, pageSize: number = 10): Promise<PaginatedResponse<Customer>> {
    const params = {
      skip: ((page - 1) * pageSize).toString(),
      limit: pageSize.toString()
    };
    
    const url = `${API_CONFIG.ENDPOINTS.CUSTOMERS}/unsupervised?${new URLSearchParams(params).toString()}`;
    return this.get<PaginatedResponse<Customer>>(url);
  }

  async removeControllerFromSupervisor(controllerId: number): Promise<any> {
    return this.post(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/controller/${controllerId}/remove-supervisor`, {});
  }

  async updateAllSupervisionStatus(): Promise<any> {
    return this.post(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/update-supervision-status`, {});
  }

  async cleanupSupervisedControllers(): Promise<{ message: string; deactivated_customers: number; deleted_contracts: number }> {
    return this.post(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/maintenance/cleanup-supervised-controllers`, {});
  }

  async getStripeCustomersByEmail(email: string, companyId?: number): Promise<any> {
    let url = `${API_CONFIG.ENDPOINTS.STRIPE_CUSTOMERS}/search-by-email?email=${encodeURIComponent(email)}`;
    if (companyId) {
      url += `&company_id=${companyId}`;
    }
    return this.get(url);
  }

  async assignStripeCustomerId(customerId: number, stripeCustomerId: string): Promise<any> {
    return this.post(`${API_CONFIG.ENDPOINTS.STRIPE_CUSTOMERS}/${customerId}/assign-stripe-customer?stripe_customer_id=${encodeURIComponent(stripeCustomerId)}`);
  }

  async createStripeCustomer(
    email: string,
    name?: string,
    companyId?: number,
    address?: string,
    vatId?: string,
    contactName?: string,
    phone?: string,
    currency?: string,
    language?: string,
    taxId?: string,
    city?: string,
    country?: string,
    postalCode?: string,
    state?: string,
    street?: string
  ): Promise<any> {
    const url = `${API_CONFIG.ENDPOINTS.STRIPE_CUSTOMERS}/create`;
    
    const body: any = {
      email: email,
    };
    
    if (name) body.name = name;
    if (companyId) body.company_id = companyId;
    if (address) body.address = address;
    if (vatId) body.vat_id = vatId;
    if (contactName) body.contact_name = contactName;
    if (phone) body.phone = phone;
    if (currency) body.currency = currency;
    if (language) body.language = language;
    if (taxId) body.tax_id = taxId;
    if (city) body.city = city;
    if (country) body.country = country;
    if (postalCode) body.postal_code = postalCode;
    if (state) body.state = state;
    if (street) body.street = street;
    
    return this.post(url, body);
  }

  async createOrLinkStripeCustomer(customerId: number, companyId?: number): Promise<any> {
    let url = `${API_CONFIG.ENDPOINTS.STRIPE_CUSTOMERS}/${customerId}/create-or-link`;
    if (companyId) {
      url += `?company_id=${companyId}`;
    }
    return this.post(url);
  }

  async listStripeCustomers(companyId?: number, limit: number = 100): Promise<any> {
    let url = `${API_CONFIG.ENDPOINTS.STRIPE_CUSTOMERS}/list?limit=${limit}`;
    if (companyId) {
      url += `&company_id=${companyId}`;
    }
    return this.get(url);
  }

  async getCustomerSyncComparison(customerId: number, companyId?: number): Promise<any> {
    let url = `${API_CONFIG.ENDPOINTS.STRIPE_CUSTOMERS}/${customerId}/sync-comparison`;
    if (companyId) {
      url += `?company_id=${companyId}`;
    }
    return this.get(url);
  }

  async syncCustomerToStripe(
    customerId: number,
    fieldsToSync: string[],
    stripeCustomerId?: string,
    createIfNotExists: boolean = false,
    companyId?: number
  ): Promise<any> {
    const payload: any = {
      system_customer_id: customerId,
      fields_to_sync: fieldsToSync,
      create_if_not_exists: createIfNotExists,
    };
    if (stripeCustomerId) {
      payload.stripe_customer_id = stripeCustomerId;
    }
    if (companyId) {
      payload.company_id = companyId;
    }
    return this.post(`${API_CONFIG.ENDPOINTS.STRIPE_CUSTOMERS}/${customerId}/sync-to-stripe`, payload);
  }
}

export const customerService = new CustomerService();